import React from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import posthog from 'posthog-js';

/**
 * Soft reader promo: one Amazon affiliate product + optional Premium upsell.
 */
export default function ReaderAffiliatePromoModal({
  product,
  placement = 'reader',
  onClose,
}) {
  if (!product || typeof document === 'undefined') return null;

  const trackClick = () => {
    posthog.capture('internal_ad_click', {
      placement,
      format: 'reader_popup',
      product_id: product.id,
      product_title: product.title,
    });
  };

  const handleClose = () => {
    posthog.capture('internal_ad_dismiss', {
      placement,
      format: 'reader_popup',
      product_id: product.id,
    });
    onClose?.();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reader-affiliate-promo-title"
      onClick={handleClose}
    >
      <div
        className="relative w-full max-w-sm rounded-xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-3 top-3 rounded px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          aria-label="Close"
        >
          ✕
        </button>

        <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
          Sponsored
        </p>
        <h2
          id="reader-affiliate-promo-title"
          className="mt-1 pr-8 text-lg font-semibold text-gray-900"
        >
          A pick for your shelf
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          Support Poppyruz when you shop — or go Premium to remove these.
        </p>

        <a
          href={product.affiliateUrl}
          target="_blank"
          rel="noopener noreferrer sponsored"
          onClick={trackClick}
          className="mt-4 flex gap-4 rounded-lg border border-gray-100 p-3 transition hover:border-violet-200 hover:bg-violet-50/40"
        >
          <div className="flex h-28 w-20 shrink-0 items-center justify-center overflow-hidden bg-gray-50">
            {product.imageUrl ? (
              <img
                src={product.imageUrl}
                alt=""
                className="h-full w-full object-contain"
              />
            ) : null}
          </div>
          <div className="min-w-0 self-center text-left">
            <p className="line-clamp-3 text-sm font-medium text-gray-900">
              {product.title}
            </p>
            <p className="mt-2 text-sm font-semibold text-violet-900">
              View on Amazon →
            </p>
          </div>
        </a>

        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Keep reading
          </button>
          <Link
            to="/premium"
            onClick={() => {
              posthog.capture('internal_ad_premium_cta', { placement });
              onClose?.();
            }}
            className="text-center text-sm font-medium text-violet-900 hover:underline"
          >
            Remove ads with Premium
          </Link>
        </div>

        <p className="mt-3 text-center text-[10px] text-gray-400">
          As an Amazon Associate, we earn from qualifying purchases.
        </p>
      </div>
    </div>,
    document.body,
  );
}
